/**
 * Placement Context tab component displaying origin metadata.
 */

import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlacementContextForm } from '@/components/forms/placement-context-form';
import { useUpdatePlacement } from '@/hooks/usePlacements';
import type { PlacementResponse } from '@/api/schemas';
import { Edit2 } from 'lucide-react';
import './context.scss';

interface PlacementContextProps {
  placement: PlacementResponse;
}

export function PlacementContext({ placement }: PlacementContextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const updatePlacement = useUpdatePlacement();
  const context = placement.context || {};

  const handleUpdateContext = async (context: Record<string, unknown>) => {
    try {
      setUpdateError(null);
      await updatePlacement.mutateAsync({
        placementId: placement.placement_id,
        data: {
          context: Object.keys(context).length > 0 ? context : null,
        },
      });
      setIsEditing(false);
      setUpdateError(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update context. Please try again.';
      setUpdateError(errorMessage);
    }
  };

  if (isEditing) {
    return (
      <Card>
        <div style={{ marginBottom: 'var(--of-space-4)' }}>
          <h3 className="h3">Edit Context Metadata</h3>
          <p className="of-text-muted">Update placement context information</p>
        </div>
        <PlacementContextForm
          initialContext={placement.context as Record<string, unknown> | null}
          onSubmit={handleUpdateContext}
          onCancel={() => {
            setIsEditing(false);
            setUpdateError(null);
          }}
          isLoading={updatePlacement.isPending}
          error={updateError}
        />
      </Card>
    );
  }

  if (!context || Object.keys(context).length === 0) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--of-space-4)' }}>
          <div>
            <h3 className="h3">Origin Metadata</h3>
            <p className="of-text-muted">No context metadata available</p>
          </div>
          <Button onClick={() => setIsEditing(true)} variant="primary">
            <Edit2 style={{ width: '1rem', height: '1rem', marginRight: 'var(--of-space-2)' }} />
            Add Context
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="placement-context">
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--of-space-4)' }}>
          <h3 className="h3">Origin Metadata</h3>
          <Button onClick={() => setIsEditing(true)} variant="secondary">
            <Edit2 style={{ width: '1rem', height: '1rem', marginRight: 'var(--of-space-2)' }} />
            Edit
          </Button>
        </div>
        <dl className="placement-context__metadata">
          {context.platform && (
            <>
              <dt>Platform</dt>
              <dd>{String(context.platform)}</dd>
            </>
          )}
          {context.placement_type && (
            <>
              <dt>Placement Type</dt>
              <dd>{String(context.placement_type)}</dd>
            </>
          )}
          {context.post_url && (
            <>
              <dt>Post URL</dt>
              <dd>
                <a
                  href={String(context.post_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  {String(context.post_url)}
                </a>
              </dd>
            </>
          )}
          {context.author_handle && (
            <>
              <dt>Author</dt>
              <dd>{String(context.author_handle)}</dd>
            </>
          )}
          {context.author && (
            <>
              <dt>Author</dt>
              <dd>{String(context.author)}</dd>
            </>
          )}
          {context.context_excerpt && (
            <>
              <dt>Excerpt</dt>
              <dd>{String(context.context_excerpt)}</dd>
            </>
          )}
          {context.excerpt && (
            <>
              <dt>Excerpt</dt>
              <dd>{String(context.excerpt)}</dd>
            </>
          )}
        </dl>
      </Card>

      <Card>
        <h3 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>
          Raw JSON
        </h3>
        <pre className="placement-context__json">
          {JSON.stringify(context, null, 2)}
        </pre>
      </Card>
    </div>
  );
}

